import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Button, Modal, Spin, Typography } from 'antd';
// Api
import { GET_TICKET_EVENT_ORDERS } from 'api/ticketing/queries';
// Types
import {
  GetTicketEventOrders,
  GetTicketEventOrdersVariables,
} from 'api/ticketing/types/GetTicketEventOrders';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  SortDirection,
  TicketEventOrderSort,
  TicketOrderKind,
} from 'api/graphql-global-types';
// Hooks
import { useBulkRefund } from './hooks/useBulkRefund';
// Helpers
import {
  getTicketingErrorMessage,
  pluralize,
  REFUNDABLE_ORDER_STATUSES,
} from 'pages/Events/helpers';

const { Paragraph, Text } = Typography;

type BulkRefundModalProps = {
  event: TicketEventDetailsFields;
  visible: boolean;
  /** started is true once the server has queued the refunds. */
  onClose: (started?: boolean) => void;
};

/** The backend refunds a cancelled event's orders in the background, so nobody has to keep a tab open. */
const BulkRefundModal = ({
  event,
  visible,
  onClose,
}: BulkRefundModalProps): JSX.Element => {
  const { data, loading, error } = useQuery<
    GetTicketEventOrders,
    GetTicketEventOrdersVariables
  >(GET_TICKET_EVENT_ORDERS, {
    variables: {
      input: {
        ticketEventId: event.id,
        kinds: [TicketOrderKind.Purchase],
        statuses: REFUNDABLE_ORDER_STATUSES,
        sortBy: TicketEventOrderSort.CreatedAt,
        direction: SortDirection.DESC,
        offset: 0,
        limit: 1,
      },
    },
    skip: !visible,
    fetchPolicy: 'network-only',
  });
  const { startRefunds, starting } = useBulkRefund();

  const paidOrders = data?.ticketEventOrders.total ?? 0;
  const nothingToRefund = !loading && !error && !paidOrders;

  const handleRefund = async (): Promise<void> => {
    const started = await startRefunds(event.id);

    if (started !== null) {
      onClose(started > 0);
    }
  };

  const renderBody = (): React.ReactNode => {
    if (loading) {
      return <Spin />;
    }

    if (error) {
      return (
        <Alert
          type="error"
          showIcon
          message={getTicketingErrorMessage(error, 'Failed to load the orders')}
        />
      );
    }

    if (!paidOrders) {
      return <Paragraph>There are no paid orders left to refund.</Paragraph>;
    }

    return (
      <>
        <Paragraph>
          Refunding {pluralize(paidOrders, 'paid order')} on{' '}
          <Text strong>{event.name}</Text> sends the money back to buyers
          through Stripe. Each order is refunded in full and its tickets are
          revoked once Stripe confirms. This can&apos;t be undone.
        </Paragraph>
        <Paragraph type="secondary">
          Orders with a ticket already scanned at the door are left as they are.
          Refunds run on the server, so you can close this page.
        </Paragraph>
      </>
    );
  };

  return (
    <Modal
      title="Refund all paid orders"
      visible={visible}
      onCancel={() => onClose()}
      onOk={handleRefund}
      okText={
        paidOrders ? `Refund ${pluralize(paidOrders, 'order')}` : 'Refund'
      }
      okButtonProps={{ danger: true, disabled: loading || !paidOrders }}
      footer={
        nothingToRefund ? (
          <Button onClick={() => onClose()}>Close</Button>
        ) : undefined
      }
      confirmLoading={starting}
      destroyOnClose
    >
      {renderBody()}
    </Modal>
  );
};

export default BulkRefundModal;
